<div class="fixed inset-0 z-1100 bg-overlay" (click)="close()"></div>
<section
  class="fixed top-1/2 left-1/2 z-1101 flex max-h-[calc(100vh-2rem)] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-visible rounded-lg border border-border border-t-[3px] border-t-primary bg-card text-fg shadow-xl"
  (click)="$event.stopPropagation()">
  <header class="flex items-start justify-between border-b border-border px-5 py-4">
    <div>
      <h3 class="m-0 text-[1.0625rem] font-bold text-fg">
        @switch (mode) {
          @case ('reconcile') { <ng-container i18n="@@topup.wizard.title.reconcile">Reconcile balance</ng-container> }
          @case ('integration-topup') { <ng-container i18n="@@topup.wizard.title.integration">Record integration topup</ng-container> }
          @case ('wallet-fund') { <ng-container i18n="@@topup.wizard.title.walletFund">Fund via operator</ng-container> }
          @default { <ng-container i18n="@@topup.wizard.title.fundMerchant">Fund merchant</ng-container> }
        }
      </h3>
      <p class="mt-1 mb-0 text-[0.8125rem] font-semibold text-primary">{{ marketCurrencyCode }}</p>
      @if (marketCountryLabel) {
        <p class="mt-0.5 mb-0 text-xs text-muted">{{ marketCountryLabel }}</p>
      }
    </div>
    <button type="button" class="cursor-pointer border-0 bg-transparent p-1 text-muted hover:text-fg" (click)="close()" aria-label="Close">
      <ng-icon name="lucideX" size="18" />
    </button>
  </header>

  <div class="border-b border-border bg-surface-muted px-5 py-4">
    <div class="mb-2 flex justify-between text-[0.8125rem] font-semibold">
      <span i18n="@@topup.wizard.progress">Progress</span>
      <span>{{ step() }} / {{ totalSteps }}</span>
    </div>
    <div class="mb-3 h-2 overflow-hidden rounded-full bg-border">
      <div class="h-full bg-primary transition-[width] duration-200 ease-in-out" [style.width.%]="progressPercent"></div>
    </div>
    <div class="flex flex-wrap gap-2">
      @for (title of stepTitles; track title; let idx = $index) {
        <span class="inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs"
          [class]="step() === idx + 1 ? 'border-primary bg-row-hover text-primary' : step() > idx + 1 ? 'border-success-border bg-card text-success-fg' : 'border-border bg-card text-muted'">
          <span class="font-bold">{{ idx + 1 }}</span>
          <span>{{ title }}</span>
        </span>
      }
    </div>
  </div>

  @if (error(); as err) {
    <p class="mx-5 mt-3 mb-0 rounded-md bg-danger-bg px-3 py-2 text-[0.8125rem] text-danger-fg">{{ err }}</p>
  }

  <div class="flex-1 overflow-x-visible overflow-y-auto p-5">
    @if (mode !== 'reconcile' && mode !== 'integration-topup' && step() === 1) {
      <div class="grid gap-3">
        @for (scope of availableScopes; track scope) {
          <button
            type="button"
            class="cursor-pointer rounded-md border bg-card p-4 text-left text-fg transition-colors hover:border-primary/50"
            [class.border-border]="!(selectedScope === scope)"
            [class.border-primary]="selectedScope === scope"
            [class.bg-row-hover]="selectedScope === scope"
            (click)="selectScope(scope)"
          >
            <span class="mb-1 block text-sm font-semibold text-fg">{{ scopeLabel(scope) }}</span>
            <span class="block text-xs leading-snug text-muted">{{ scopeHint(scope) }}</span>
          </button>
        }
      </div>
    }

    @if (
      (mode === 'integration-topup' && step() === 1) ||
      (mode !== 'reconcile' && mode !== 'integration-topup' && step() === 2)
    ) {
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.amount">Amount</span>
          <input uiInput type="number" step="0.01" min="0" [(ngModel)]="amount" [disabled]="saving()" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.fundingSource">Funding source</span>
          <select uiInput [(ngModel)]="fundingSource" [disabled]="saving()">
            @for (src of fundingSources; track src) {
              <option [value]="src">{{ fundingLabel(src) }}</option>
            }
          </select>
        </label>
        @if (needsWallet() && mode !== 'wallet-fund') {
          <label class="flex flex-col gap-1 sm:col-span-2">
            <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.wallet">Merchant wallet</span>
            <div class="relative z-1 flex items-center gap-2 overflow-visible [&>lib-autocomplete]:flex-1">
              <lib-autocomplete
                listPath="finances/wallets/"
                [value]="walletDisplay"
                [minLength]="1"
                [extraQueryParams]="walletSearchParams"
                placeholder="Search wallet…"
                (itemSelect)="selectWallet($event)"
              />
              @if (walletIdValue) {
                <button type="button" class="cursor-pointer border-0 bg-transparent text-[0.8125rem] whitespace-nowrap text-primary" (click)="clearWallet()">Clear</button>
              }
            </div>
          </label>
        }
        @if (mode === 'wallet-fund' && selectedScope === 'WALLET_AND_INTEGRATION') {
          <label class="flex flex-col gap-1 sm:col-span-2">
            <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.entity">Financial entity</span>
            <div class="relative z-1 flex items-center gap-2 overflow-visible [&>lib-autocomplete]:flex-1">
              <lib-autocomplete
                listPath="finances/entities/"
                [value]="financialEntityDisplay"
                [minLength]="1"
                placeholder="Search entity…"
                (itemSelect)="selectEntity($event)"
              />
              @if (financialEntityId) {
                <button type="button" class="cursor-pointer border-0 bg-transparent text-[0.8125rem] whitespace-nowrap text-primary" (click)="clearEntity()">Clear</button>
              }
            </div>
          </label>
        }
        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.datetime">Datetime</span>
          <input uiInput type="datetime-local" [(ngModel)]="toppedUpAt" [disabled]="saving()" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.reference">Reference</span>
          <input uiInput type="text" [(ngModel)]="reference" [disabled]="saving()" />
        </label>
        <label class="flex flex-col gap-1 sm:col-span-2">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.notes">Notes</span>
          <textarea uiInput rows="2" [(ngModel)]="notes" [disabled]="saving()"></textarea>
        </label>
      </div>
    }

    @if (mode === 'reconcile' && step() === 1) {
      <div class="grid gap-3">
        <button
          type="button"
          class="cursor-pointer rounded-md border bg-card p-4 text-left text-fg transition-colors hover:border-primary/50"
          [class.border-border]="!(reconcileSource === 'manual')"
          [class.border-primary]="reconcileSource === 'manual'"
          [class.bg-row-hover]="reconcileSource === 'manual'"
          (click)="reconcileSource = 'manual'"
        >
          <span class="mb-1 block text-sm font-semibold text-fg" i18n="@@topup.reconcile.manual">Manual entry</span>
          <span class="block text-xs leading-snug text-muted" i18n="@@topup.reconcile.manualHint">Enter the balance reported by the provider</span>
        </button>
        @if (supportsBalanceApi) {
          <button
            type="button"
            class="cursor-pointer rounded-md border bg-card p-4 text-left text-fg transition-colors hover:border-primary/50"
            [class.border-border]="!(reconcileSource === 'fetch')"
            [class.border-primary]="reconcileSource === 'fetch'"
            [class.bg-row-hover]="reconcileSource === 'fetch'"
            (click)="reconcileSource = 'fetch'"
          >
            <span class="mb-1 block text-sm font-semibold text-fg" i18n="@@topup.reconcile.fetch">Fetch from API</span>
            <span class="block text-xs leading-snug text-muted" i18n="@@topup.reconcile.fetchHint">Pull the latest balance from the provider integration</span>
          </button>
        }
      </div>
    }

    @if (mode === 'reconcile' && step() === 2 && reconcileSource === 'manual') {
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.providerBalance">Provider balance</span>
          <input uiInput type="number" step="0.01" [(ngModel)]="reconcileBalance" [disabled]="saving()" />
        </label>
        <label class="flex flex-col gap-1 sm:col-span-2">
          <span class="text-xs font-semibold tracking-wide text-muted uppercase" i18n="@@topup.field.notes">Notes</span>
          <textarea uiInput rows="2" [(ngModel)]="notes" [disabled]="saving()"></textarea>
        </label>
      </div>
    }

    @if (mode === 'reconcile' && step() === 2 && reconcileSource === 'fetch') {
      <p class="m-0 text-sm text-muted" i18n="@@topup.reconcile.fetchConfirm">
        The next step will trigger a provider balance fetch for {{ marketCurrencyCode }}.
      </p>
    }

    @if (step() === totalSteps) {
      <div class="rounded-md border border-border bg-surface-muted p-4">
        <h4 class="mt-0 mb-3 text-[0.9375rem] font-semibold" i18n="@@topup.review.heading">Review before confirming</h4>
        @if (mode !== 'reconcile') {
          <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
            <span i18n="@@topup.review.amount">Amount</span>
            <strong>{{ formatAmount(amount) }}</strong>
          </div>
          <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
            <span i18n="@@topup.review.market">Market</span>
            <strong>{{ marketCurrencyCode }}</strong>
          </div>
          @if (mode !== 'integration-topup') {
            <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
              <span i18n="@@topup.review.scope">Scope</span>
              <strong>{{ scopeLabel(selectedScope) }}</strong>
            </div>
            <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
              <span i18n="@@topup.review.accounting">Accounting</span>
              <span class="max-w-[60%] text-right text-xs text-muted">{{ scopeHint(selectedScope) }}</span>
            </div>
          }
          @if (walletDisplay) {
            <div class="flex items-start justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
              <span i18n="@@topup.review.wallet">Wallet</span>
              <div class="flex flex-col items-end gap-0.5 text-right">
                <strong>{{ walletDisplay }}</strong>
                @if (walletCountryDisplay) {
                  <span class="text-xs font-normal text-muted">{{ walletCountryDisplay }}</span>
                }
              </div>
            </div>
          }
          @if (financialEntityDisplay) {
            <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
              <span i18n="@@topup.review.entity">Entity</span>
              <strong>{{ financialEntityDisplay }}</strong>
            </div>
          }
        } @else {
          <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
            <span i18n="@@topup.review.method">Method</span>
            <strong>{{ reconcileSource === 'fetch' ? 'Fetch from API' : 'Manual snapshot' }}</strong>
          </div>
          @if (reconcileSource === 'manual') {
            <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
              <span i18n="@@topup.review.reported">Reported balance</span>
              <strong>{{ formatAmount(reconcileBalance) }}</strong>
            </div>
          }
          @if (ledgerBalance) {
            <div class="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
              <span i18n="@@topup.review.ledger">Ledger balance</span>
              <strong>{{ formatAmount(ledgerBalance) }}</strong>
            </div>
          }
        }
      </div>
    }
  </div>

  <footer class="flex items-center justify-between gap-3 rounded-b-lg border-t border-border bg-surface-muted px-5 py-4">
    <button type="button" uiButton variant="outline" (click)="close()" [disabled]="saving()" i18n="@@topup.action.cancel">
      Cancel
    </button>
    <div class="flex gap-2">
      @if (step() > 1) {
        <button type="button" uiButton variant="outline" (click)="prevStep()" [disabled]="saving()" i18n="@@topup.action.back">
          Back
        </button>
      }
      @if (step() < totalSteps) {
        <button type="button" uiButton variant="primary" (click)="nextStep()" [disabled]="saving()" i18n="@@topup.action.next">
          Next
        </button>
      } @else {
        <button type="button" uiButton variant="primary" (click)="submit()" [disabled]="saving()">
          @if (saving()) {
            <ng-container i18n="@@topup.action.saving">Saving…</ng-container>
          } @else if (mode === 'reconcile' && reconcileSource === 'fetch') {
            <ng-container i18n="@@topup.action.fetch">Fetch &amp; reconcile</ng-container>
          } @else {
            <ng-container i18n="@@topup.action.confirm">Confirm</ng-container>
          }
        </button>
      }
    </div>
  </footer>
</section>
